import { SERVER_DOMAIN } from "@/config/config.js";
import fetch from "isomorphic-fetch";
import { toast } from "react-toastify";
import history from "helpers/history";
import moment from "moment";
import { refreshToken } from "store/actions";
import { toCapitalizeChart1 } from "utils/common";
import { logError } from "services/logger";
import {
  isLoading,
  redirectUnauthenticated,
} from "helpers/helpers";

const configError = {
  autoClose: 3000,
};

const handleUnauthorized = () => {
  if (window.localStorage.is_refresh) return;
  if (window.localStorage.getItem("refresh_token")) {
    refreshToken();
    return;
  }
  redirectUnauthenticated();
};

const fetchV3Error = (status, data = null) => ({ status, data });

export class ApiError extends Error {
  constructor(status, data = null) {
    super(`HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function assertFetchV3Success(resp) {
  if (resp == null) return null;
  if (resp.timestamp != null) return resp;
  throw new ApiError(resp.status ?? 0, resp.data ?? null);
}

export const fetchV3Payload = (resp) => {
  const ok = assertFetchV3Success(resp);
  return ok?.data;
};

const sanitizeLogContext = ({ url, method, error }) => {
  let safeUrl = url;
  try {
    const parsed = new URL(url);
    safeUrl = `${parsed.origin}${parsed.pathname}`;
  } catch {
    safeUrl = String(url).split("?")[0];
  }
  const ctx = { method, url: safeUrl };
  if (error && typeof error === "object" && "status" in error) {
    ctx.status = error.status;
  }
  return ctx;
};

export const isEmptySuccess = (resp) => {
  if (resp == null) return true;
  if (resp.timestamp != null) return !resp.data;
  return false;
};

export const fetchV3 = (
  {
    url,
    method = "GET",
    query = "",
    body = null,
    headers = {},
    respFile = false,
    isSkippedRedirect403 = false,
    skipErrorToast = false,
    silent = false,
    useLoading = false,
    noToken = false,
  },
  notStringify = false,
) => {
  const quiet = silent || skipErrorToast;
  if (useLoading && !silent) isLoading(true, url);
  const clearLoading = () => {
    if (useLoading && !silent) isLoading(false, url);
  };

  let requestParams = {
    method: method,
    headers: headers,
  };
  const accessToken = window.localStorage.getItem("access_token");
  if (!noToken && accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }
  if (method !== "GET" && body) {
    if (notStringify) {
      requestParams.body = body;
    } else {
      requestParams.body = JSON.stringify(body);
      headers["Content-Type"] = "application/json";
    }
  }
  if (method === "GET" && !query && body && typeof body === "object") {
    const params = new URLSearchParams();
    Object.keys(body).forEach((key) => {
      const value = body[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });
    const serialized = params.toString();
    query = serialized ? `?${serialized}` : "";
  }
  const requestUrl = `${SERVER_DOMAIN}api/v1/${url}${query}`;
  const logContext = { url: requestUrl, method };
  return fetch(requestUrl, requestParams)
    .then((resp) => {
      clearLoading();
      if (resp.status >= 200 && resp.status < 400) {
        if (resp.status === 204) return;
        if (respFile) return resp.blob();
        return resp
          .json()
          .then((result) => {
            return { timestamp: moment().format(), data: result };
          })
          .catch(() =>
            Promise.reject(new ApiError(resp.status, { parse: "invalid_json" })),
          );
      } else {
        if (resp.status === 409) {
          logError({
            message: "Conflict",
            context: sanitizeLogContext(logContext),
          });
          return Promise.reject(new ApiError(resp.status));
        }
        if (resp.status === 404) {
          logError({
            message: "Not found",
            context: sanitizeLogContext(logContext),
          });
          return Promise.reject(new ApiError(resp.status));
        }
        if (resp.status === 401) {
          logError({ message: "Unauthorized", context: logContext });
          handleUnauthorized();
          return Promise.reject(fetchV3Error(resp.status));
        } else if (resp.status === 500) {
          logError({ message: "Server error", context: logContext });
          if (!quiet) toast(resp.statusText, configError);
          return Promise.reject(fetchV3Error(resp.status));
        } else if (resp.status === 503) {
          logError({
            message: "System is not available at the moment",
            context: logContext,
          });
          if (!quiet)
            toast("System is not available at the moment", configError);
          return Promise.reject(fetchV3Error(resp.status));
        } else if (resp.status === 502) {
          logError({
            message: "Server returned a status 502",
            context: logContext,
          });
          if (!quiet) toast("Server returned a status 502", configError);
          return Promise.reject(fetchV3Error(resp.status));
        } else if (resp.status === 504) {
          if (!quiet) toast("Server returned a status 504", configError);
          return Promise.reject(fetchV3Error(resp.status));
        } else if (resp.status === 400) {
          if (resp.json) {
            return resp.json().then((data) => {
              let error = "";
              Object.keys(data).map((v) => {
                const item = data[v];
                if (typeof item === "string") {
                  if (typeof +v === "number") error += item;
                  else
                    error +=
                      " " +
                      toCapitalizeChart1(v.replace(/_/g, " ")) +
                      " - " +
                      item.toLowerCase();
                } else {
                  if (item.length) {
                    error +=
                      " " +
                      toCapitalizeChart1(v.replace(/_/g, " ")) +
                      " - " +
                      item.join(", ").toLowerCase();
                  } else {
                    if (typeof item === "object") {
                      for (let key in item) {
                        if (Array.isArray(item[key]))
                          error +=
                            " " +
                            toCapitalizeChart1(key.replace(/_/g, " ")) +
                            " - " +
                            item[key].join(", ").toLowerCase();
                        else error += JSON.stringify(item[key]);
                      }
                    } else error += JSON.stringify(item);
                  }
                }
              });
              logError({
                message: "Bad request",
                context: sanitizeLogContext({
                  ...logContext,
                  error: { status: resp.status },
                }),
              });
              if (!quiet) {
                toast(error, { containerId: "test", ...configError });
              }
              return Promise.reject(new ApiError(resp.status, data));
            });
          } else {
            logError({
              message: "Bad request",
              context: sanitizeLogContext(logContext),
            });
            if (!quiet)
              toast("Something went wrong. Please check your data", configError);
            return Promise.reject(new ApiError(resp.status));
          }
        } else if (resp.status === 403) {
          return resp.json().then((response) => {
            logError({
              message: "Forbidden",
              context: sanitizeLogContext({
                ...logContext,
                error: { status: resp.status },
              }),
            });
            if (!isSkippedRedirect403 && !silent) {
              if (
                response.detail ===
                "You do not have permission to perform this action."
              ) {
                if (!quiet) toast(response.detail, configError);
                history.goBack();
                throw "";
              }
              if (response.detail && !quiet)
                toast(response.detail, configError);
            }
            return Promise.reject(new ApiError(resp.status, response));
          });
        } else {
          const isJSON = !!resp.json;
          if (isJSON) {
            try {
              return resp
                .json()
                .then((error) => {
                  return Promise.reject(fetchV3Error(resp.status, error));
                })
                .catch((error) => {
                  return Promise.reject(
                    fetchV3Error(
                      resp.status,
                      error && typeof error === "object" ? error : null,
                    ),
                  );
                });
            } catch {
              return Promise.reject(fetchV3Error(resp.status));
            }
          }
          return Promise.reject(fetchV3Error(resp.status));
        }
      }
    })
    .catch((err) => {
      clearLoading();
      if (
        err &&
        typeof err === "object" &&
        Object.prototype.hasOwnProperty.call(err, "status") &&
        Object.prototype.hasOwnProperty.call(err, "data")
      ) {
        logError({
          message: "Error",
          context: sanitizeLogContext({ ...logContext, error: err }),
        });
        return Promise.reject(err);
      }
      if (err === "") {
        return Promise.reject(new ApiError(403));
      }
      logError({
        message: "Error",
        context: sanitizeLogContext({ ...logContext, error: err }),
      });
      return Promise.reject(
        fetchV3Error(0, err && typeof err === "object" ? err : null),
      );
    });
};
